import { useEffect, useRef } from 'react';
import { wireGraph } from './useGraphWiring';
import { ensureMasterChainIdempotent, ensureDestinationAlive } from '../graphNodes';
import { disposeStems } from './refs';
import { disconnectAll as disconnectStems } from './graphBuilder';
import { loadBuffers, makeScheduled, scheduleStartAll, stopAll, onSwapCacheMaintenance } from '../bufferTransport';
import { logDecodedCacheStats, logCompressedCacheStats } from '../bufferTransport/caches';
import { ensureRubberBandWorklet, reattachRbIfNeeded } from '../bufferTransport/worklets';
import { computeSpeedMapping } from '../speedMapping';
import { disposeScheduled } from '../bufferTransport';
import { L } from '../../../utils/logger';
import { probeRmsDb, openGateWhenSignal } from './helpers/health';
import { diagOn, logMasterChain, logScheduled, logUnexpectedSilence, startHeartbeat } from './helpers/diag';
import { makeTransportGate } from './helpers/transportGate';
import coldStart from './coldStart';
import { getCtx, ctxOk, getCtxGen } from '../ctxSingleton';

// Chromium-only memory budget check; safe no-op elsewhere
async function shouldFlushCtx(thresholdMB: number = 90): Promise<boolean> {
  try {
    const anyPerf: any = (typeof performance !== 'undefined' ? performance : undefined) as any;
    if (!anyPerf || typeof anyPerf.measureUserAgentSpecificMemory !== 'function') return false;
    const r = await anyPerf.measureUserAgentSpecificMemory();
    const bytes = (r && typeof r.bytes === 'number') ? r.bytes : 0;
    return (bytes / (1024 * 1024)) > thresholdMB;
  } catch { return false; }
}

/** Low-overhead gap monitor: checks every 150ms and logs only on state changes */
function startGapMonitor(
  an: AnalyserNode | null,
  ctx: () => { maintainPitch: boolean; pitchSt: number; rate: number; expectedPlaying?: boolean },
  resync?: () => void,
) {
  if (!an) return;
  const THRESH_DB = -60; // consider gap if below -60 dBFS
  const ENTER_MS = 250; // must be quiet for at least this duration to act
  const RESYNC_COOLDOWN_MS = 3000; // avoid resync loops
  let silentSince: number | null = null;
  let wasSilent = false;
  let timer: any = null;
  let lastResyncAt = 0;
  const tick = () => {
    try {
      // In background tabs, timer and UI reads are throttled; skip gap detection to avoid false resyncs
      try {
        const hidden = typeof document !== 'undefined' && document.visibilityState !== 'visible';
        if (hidden) { wasSilent = false; silentSince = null; return; }
      } catch {}
      const db = probeRmsDb(an);
      const { maintainPitch, pitchSt, rate, expectedPlaying } = ctx();
      const low = db != null && db < THRESH_DB;
      const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
      if (low) {
        if (silentSince == null) silentSince = now;
        const dur = now - silentSince;
        if (!wasSilent && dur > ENTER_MS && maintainPitch) {
          try { L.warn('media:gap', 'silence-gap.enter', { db: (db as any)?.toFixed?.(1), ms: Math.round(dur), pitchSt, rate }); } catch {}
          wasSilent = true;
          // Auto-resync if we expected to be playing
          if (expectedPlaying && typeof resync === 'function' && (now - lastResyncAt) > RESYNC_COOLDOWN_MS) {
            try { L.info('media:gap', 'resync.attempt'); } catch {}
            try { resync(); } catch {}
            lastResyncAt = now;
          }
        }
      } else {
        if (wasSilent) {
          const dur = silentSince ? (now - silentSince) : 0;
          try { L.info('media:gap', 'silence-gap.exit', { ms: Math.round(dur) }); } catch {}
        }
        wasSilent = false;
        silentSince = null;
      }
    } catch {}
  };
  timer = setInterval(tick, 150);
  // auto-stop after ~20s to limit overhead during long sessions
  setTimeout(() => { try { if (timer) clearInterval(timer); } catch {} }, 20000);
}

interface Args {
  tracks: Array<{ role: string; url: string; useRb?: boolean }>;
  refs: any;
  masterVolume: number;
  masterFftOrder: number;
  masterHighpassHz: number;
  masterLowpassHz: number;
  eqQ: number;
  eqEnabled: boolean;
  eqHpfEnabled: boolean;
  eqLpfEnabled: boolean;
  freqResponseEnabled: boolean;
  getEffectiveGain: (role: string) => number;
  bufferModeRef: React.MutableRefObject<boolean>;
  buffersRef: React.MutableRefObject<Record<string, AudioBuffer> | null>;
  buffersSigRef: React.MutableRefObject<string>;
  loadRunIdRef: React.MutableRefObject<number>;
  scopeVersionRef: React.MutableRefObject<number>;
  resumeAfterLoadRef: React.MutableRefObject<boolean>;
  scheduledRef: React.MutableRefObject<any>;
  scheduledMetaRef: React.MutableRefObject<{ version: number; sig: string } | null>;
  logicalStartRef: React.MutableRefObject<number>;
  startedAtRef: React.MutableRefObject<number>;
  status: 'idle' | 'loading' | 'playing' | 'paused';
  masterPlaybackRate: number;
  maintainPitch?: boolean;
  pauseAll: () => void;
  setDuration: (n: number) => void;
  setStatus: (s: any) => void;
  setCurrentTime?: (n: number) => void;
  userPausedRef: React.MutableRefObject<boolean>;
  setStemErrors?: (errs: Record<string, { status?: number; message: string; url?: string }> | null) => void;
}

export function useWireAndLoad(args: Args) {
  const {
    tracks, refs, masterVolume, masterFftOrder, masterHighpassHz, masterLowpassHz, eqQ,
    eqEnabled, eqHpfEnabled, eqLpfEnabled, freqResponseEnabled, getEffectiveGain,
    bufferModeRef, buffersRef, buffersSigRef, loadRunIdRef, scopeVersionRef, resumeAfterLoadRef,
  scheduledRef, scheduledMetaRef, logicalStartRef, startedAtRef, status, masterPlaybackRate, maintainPitch, pauseAll,
    setDuration, setStatus, setCurrentTime, userPausedRef,
  } = args;

  const swapIdRef = useRef(0);

  useEffect(() => {
    if (!tracks.length) {
      // No dispose here; we only log and pause (prevents hard mute thrash)
  L.info('media:scope', 'empty-tracks-transient?');
      try {
        if (scheduledRef.current) {
          const keys = Object.keys(scheduledRef.current || {});
          L.info('media:scope', 'disposing-empty-tracks', { roles: keys });
          try { stopAll(scheduledRef.current); } catch {}
          disposeScheduled(scheduledRef.current);
        }
      } catch {}
      scheduledRef.current = null;
      buffersRef.current = null;
      buffersSigRef.current = '';
      setStatus('paused');
      setDuration(0);
      try { if (setCurrentTime) setCurrentTime(0); } catch {}
      return;
    }

    if (!refs.audioCtxRef.current || !ctxOk(refs.audioCtxRef.current)) {
      refs.audioCtxRef.current = getCtx();
    }
    const ctx = refs.audioCtxRef.current!;

  // Proactively dispose old element-path stem nodes before wiring new scope to prevent leaks.
  // NOTE: This clears refs.analyzersRef, which the UI meters read. In buffer mode, we immediately
  // repopulate it from the active scheduled stems to keep meters alive during parameter tweaks.
  try { disposeStems(ctx, { sourcesRef: refs.sourcesRef, gainsRef: refs.gainsRef, analyzersRef: refs.analyzersRef, delaysRef: refs.delaysRef }); } catch {}
  // If buffer mode is active and we already have scheduled stems, restore the analyser map now.
  try {
    if (bufferModeRef.current && scheduledRef.current) {
      const sched = scheduledRef.current;
      const ana: Record<string, AnalyserNode> = {} as any;
      for (const role of Object.keys(sched)) { ana[role] = sched[role].analyser; }
      refs.analyzersRef.current = ana;
    }
  } catch {}

    if (bufferModeRef.current) {
      // Enforce buffer-only path: ensure master chain but skip element stems
  const rewired0 = ensureMasterChainIdempotent(
        ctx,
        {
          masterGainRef: refs.masterGainRef,
          gateGainRef: refs.gateGainRef,
          teeGainRef: refs.teeGainRef,
          masterAnalyserPreRef: refs.masterAnalyserPreRef,
          masterHighpassRef: refs.masterHighpassRef,
          masterFilterRef: refs.masterFilterRef,
          masterAnalyserRef: refs.masterAnalyserRef,
        },
        { masterVolume, masterFftOrder, masterHighpassHz, masterLowpassHz, q: eqQ },
        { eqEnabled, eqHpfEnabled, eqLpfEnabled, freqResponseEnabled: (freqResponseEnabled || refs?.masterEnvelopeEnabled) }
      );
  // Affirm destination hop proactively to avoid silent graph
  try { ensureDestinationAlive(ctx, { masterGainRef: refs.masterGainRef, masterHighpassRef: refs.masterHighpassRef, masterFilterRef: refs.masterFilterRef }); } catch {}
    try { if (rewired0) logMasterChain(refs, 'after-ensureMasterChain'); } catch {}
    try { if (diagOn()) startHeartbeat(refs, ctx, 1000); } catch {}
      // If any element stems were previously wired, disconnect them now
      try {
        if (refs.stems && refs.stems.length) {
          disconnectStems(refs.masterGraph, refs.stems);
          refs.stems = [];
        }
      } catch {}
    } else {
  wireGraph(
        ctx,
        tracks,
        refs,
        { masterVolume, masterFftOrder, masterHighpassHz, masterLowpassHz, q: eqQ },
        { eqEnabled, eqHpfEnabled, eqLpfEnabled, freqResponseEnabled: (freqResponseEnabled || refs?.masterEnvelopeEnabled) },
        getEffectiveGain,
      );
  // Re-affirm destination in element path as well
  try { ensureDestinationAlive(ctx, { masterGainRef: refs.masterGainRef, masterHighpassRef: refs.masterHighpassRef, masterFilterRef: refs.masterFilterRef }); } catch {}
    }

    if (bufferModeRef.current) {
      const sig = tracks.map(t => `${t.role}|${t.url}`).join('||');

      // CASE 1: New tracks signature → fresh load
      if (sig && sig !== buffersSigRef.current) {
        L.info('media:scope', 'scope-change', { sig, roles: tracks.map(t=>t.role) });
        (async () => {
          // Track these across try/catch to abort on stale work
          let thisRun: number | undefined;
          let thisScope: number | undefined;
          const mySwapId = ++swapIdRef.current;
          let myGen = getCtxGen();
          // Global swap sequence to cancel stale async work from past scopes
          try { (refs as any)._swapSeq = (((refs as any)._swapSeq ?? 0) + 1); } catch {}
          const mySeq = (() => { try { return (refs as any)._swapSeq as number; } catch { return mySwapId; } })();
          try {
            // Dispose any existing scheduled stems immediately to avoid reusing finished nodes during fast swaps
            try {
              if (scheduledRef.current) {
                const keys = Object.keys(scheduledRef.current || {});
                L.info('media:scope', 'disposing-before-load', { roles: keys });
                try { stopAll(scheduledRef.current); } catch {}
                disposeScheduled(scheduledRef.current);
                scheduledRef.current = null;
              }
            } catch {}
            // Cache policy on swap: force flush to A/B memory behavior during leak triage
            // Drop previous decoded buffer map reference before flushing caches to speed up GC
            try { buffersRef.current = null; } catch {}
            try { buffersSigRef.current = ''; } catch {}
            try { logDecodedCacheStats('pre-flush'); logCompressedCacheStats('pre-flush'); } catch {}
            try { onSwapCacheMaintenance(false); } catch {}
            try { logDecodedCacheStats('post-flush'); logCompressedCacheStats('post-flush'); } catch {}
            // Decide whether to perform a hard AudioContext recycle based on budget and swap cadence
            const budgetMB = (() => { try { return Number(localStorage.getItem('MEDIA_JS_BUDGET_MB') || 90); } catch { return 90; } })();
            // Default: disable cadence-based hard flush (best practice: keep one context)
            let flushEvery = (() => {
              try {
                const raw = localStorage.getItem('MEDIA_CTX_FLUSH_EVERY');
                if (raw == null) return Number.POSITIVE_INFINITY; // unset => disabled
                const n = Number(raw);
                return (!isFinite(n) || n <= 0) ? Number.POSITIVE_INFINITY : n;
              } catch { return Number.POSITIVE_INFINITY; }
            })();
            const swapsSinceHard = (() => { try { return Number((refs as any)._swapsSinceHardFlush || 0); } catch { return 0; } })();
            const highMem = await shouldFlushCtx(budgetMB);
            const doHardFlush = !!highMem || (swapsSinceHard >= flushEvery);
            try { L.info('media:scope', 'flush-decision', { budgetMB, flushEvery, swapsSinceHard, highMem, doHardFlush }); } catch {}

            // 🔥 COLD START: tear everything down (hard when budget exceeded or cadence threshold reached)
            await coldStart(refs, {
              hard: doHardFlush,
              bufferModeRef,
              scheduledRef,
              disposeScheduled,
              stopAll,
              pauseAll,
              setStatus,
              setCurrentTime: setCurrentTime || (()=>{}),
            });
            // If the AudioContext generation changed (hard flush), adopt and continue
            {
              const newGen = getCtxGen();
              if (newGen !== myGen) {
                // Only continue if this swap is still current
                if (mySwapId !== swapIdRef.current) return;
                try { if (((refs as any)._swapSeq ?? mySeq) !== mySeq) return; } catch {}
                myGen = newGen;
              }
            }
            // Track swap cadence toward next hard flush
            try { (refs as any)._swapsSinceHardFlush = doHardFlush ? 0 : ((swapsSinceHard | 0) + 1); } catch {}
            if (mySwapId !== swapIdRef.current) return; // aborted by a newer swap
            try { if (((refs as any)._swapSeq ?? mySeq) !== mySeq) return; } catch {}

            // From here we’re clean; record the new signature we’re about to load
            buffersSigRef.current = sig;

            // Soft cold start: auto-resume only if we were previously playing
            resumeAfterLoadRef.current = (status === 'playing');
            logicalStartRef.current = 0;
            startedAtRef.current = refs.audioCtxRef.current?.currentTime || 0;
            setStatus((p: any) => (p === 'loading' ? p : 'loading'));
            if (setCurrentTime) setCurrentTime(0);

            thisRun = ++loadRunIdRef.current;
            thisScope = scopeVersionRef.current;
            try { L.info('media:scope', 'scheduled-ref-after-coldStart', { present: !!scheduledRef.current }); } catch {}

            // After a cold start, reacquire a live ctx. Only resume if user has interacted (avoids autoplay warnings)
            let ctx2 = getCtx();
            refs.audioCtxRef.current = ctx2;
            try {
              const ua: any = (typeof navigator !== 'undefined') ? (navigator as any).userActivation : undefined;
              const canResume = !!ua && (ua.isActive === true || ua.hasBeenActive === true);
              if (ctx2.state === 'suspended' && canResume) { await ctx2.resume().catch(() => {}); }
            } catch {}
            // Reacquire/adopt live context after await to avoid stale references
            {
              const newGen = getCtxGen();
              if (newGen !== myGen) {
                if (mySwapId !== swapIdRef.current) return;
                try { if (((refs as any)._swapSeq ?? mySeq) !== mySeq) return; } catch {}
                myGen = newGen;
              }
              ctx2 = getCtx();
            }
            // Rebuild the master chain before proceeding.
            const rewired1 = ensureMasterChainIdempotent(
              ctx2,
              {
                masterGainRef: refs.masterGainRef,
                gateGainRef: refs.gateGainRef,
                teeGainRef: refs.teeGainRef,
                masterAnalyserPreRef: refs.masterAnalyserPreRef,
                masterHighpassRef: refs.masterHighpassRef,
                masterFilterRef: refs.masterFilterRef,
                masterAnalyserRef: refs.masterAnalyserRef,
              },
              { masterVolume, masterFftOrder, masterHighpassHz, masterLowpassHz, q: eqQ },
              { eqEnabled, eqHpfEnabled, eqLpfEnabled, freqResponseEnabled: (freqResponseEnabled || refs?.masterEnvelopeEnabled) }
            );
            try { ensureDestinationAlive(ctx2, { masterGainRef: refs.masterGainRef, masterHighpassRef: refs.masterHighpassRef, masterFilterRef: refs.masterFilterRef }); } catch {}
            try { if (rewired1) logMasterChain(refs, 'after-ensureMasterChain:post-coldStart'); } catch {}
            try { if (diagOn()) startHeartbeat(refs, ctx2, 1000); } catch {}

            try { logDecodedCacheStats('pre-load'); logCompressedCacheStats('pre-load'); } catch {}
            const bufMap = await loadBuffers(ctx2, tracks.map(t => ({ role: t.role, url: t.url })));
            try { logDecodedCacheStats('post-load'); logCompressedCacheStats('post-load'); } catch {}
            // Clear any previous errors after a successful load
            try { args.setStemErrors?.(null); } catch {}
            {
              const newGen = getCtxGen();
              if (newGen !== myGen) {
                if (mySwapId !== swapIdRef.current) return;
                try { if (((refs as any)._swapSeq ?? mySeq) !== mySeq) return; } catch {}
                myGen = newGen;
              }
            }
            if (mySwapId !== swapIdRef.current) return; // aborted by a newer swap
            try { if (((refs as any)._swapSeq ?? mySeq) !== mySeq) return; } catch {}
            if (loadRunIdRef.current !== thisRun) return;
            if (scopeVersionRef.current !== thisScope) return;
            buffersRef.current = bufMap;
            // Per-role validation: bail early if any buffer is invalid or has zero duration
            try {
              for (const [role, buf] of Object.entries(bufMap as any)) {
                const dur = (buf as any)?.duration;
                if (!buf || typeof dur !== 'number' || !isFinite(dur) || dur <= 0) {
                  try { L.error('media:load', 'invalid-buffer', { role, hasBuf: !!buf, duration: dur ?? null }); } catch {}
                  scheduledRef.current = null;
                  setStatus((p: any) => (p === 'paused' ? p : 'paused'));
                  setDuration(0);
                  resumeAfterLoadRef.current = false;
                  return;
                }
              }
            } catch {}
            try { L.info('media:scope', 'scheduled-ref-before-make', { present: !!scheduledRef.current }); } catch {}

            // Log buffer durations/lengths per role to catch stale/invalid buffers
            try {
              for (const r of tracks.map(t => t.role)) {
                const b: any = (bufMap as any)[r];
                L.info('media:scope', 'swap-check', { role: r, duration: b?.duration ?? null, length: b?.length ?? null });
              }
            } catch {}

            const vals = Object.values(bufMap).map((b: any) => b?.duration).filter((v: any) => typeof v === 'number' && isFinite(v) && v > 0) as number[];
            const maxDur = vals.length ? Math.max(...vals) : 0;
            if (!vals.length || !(maxDur > 0)) {
              try { L.warn('media:scope', 'invalid-buffers', { vals }); } catch {}
              setDuration(0);
              setStatus((p: any) => (p === 'paused' ? p : 'paused'));
              resumeAfterLoadRef.current = false;
              return; // do not schedule invalid buffers
            }

            if (!refs.masterGainRef.current) return;
            // Reset gate/analyser diagnostic state before (re)creating scheduled sources
            try { (refs as any)._silentStarts = 0; } catch {}
            try { (refs as any)._gateProbeLastDb = null; } catch {}
            try { (refs as any)._gateProbeLastLogTs = 0; } catch {}
            try { (refs as any)._hbId = 0; } catch {}
            try { if (((refs as any)._swapSeq ?? mySeq) !== mySeq) return; } catch {}
            scheduledRef.current = makeScheduled(ctx2, bufMap, tracks.map(t => t.role), refs.masterGainRef.current);
            if (mySwapId !== swapIdRef.current) { try { disposeScheduled(scheduledRef.current); } catch {} scheduledRef.current = null; return; }
            try { logScheduled(refs, 'after-makeScheduled'); } catch {}
            // Ensure RB remains in path for newly created sources
            try { reattachRbIfNeeded(ctx2, scheduledRef.current); } catch {}

            if (scheduledRef.current) {
              // Attach Rubber Band worklets to stems only when pitch preservation is enabled
              if (maintainPitch) {
                await ensureRubberBandWorklet(ctx2, scheduledRef.current);
                {
                  const newGen = getCtxGen();
                  if (newGen !== myGen) {
                    if (mySwapId !== swapIdRef.current) { try { disposeScheduled(scheduledRef.current); } catch {}; scheduledRef.current = null; return; }
                    try { if (((refs as any)._swapSeq ?? mySeq) !== mySeq) { try { disposeScheduled(scheduledRef.current); } catch {}; scheduledRef.current = null; return; } } catch {}
                    // Adopt the new generation and reacquire ctx for subsequent operations
                    myGen = newGen;
                    ctx2 = getCtx();
                  }
                }
                if (mySwapId !== swapIdRef.current) { try { disposeScheduled(scheduledRef.current); } catch {} scheduledRef.current = null; return; }
                try { if (((refs as any)._swapSeq ?? mySeq) !== mySeq) { try { disposeScheduled(scheduledRef.current); } catch {}; scheduledRef.current = null; return; } } catch {}

                // Wait briefly for all attached RB nodes to report 'live' (observed non-zero input)
                try {
                  const start = (typeof performance !== 'undefined' ? performance.now() : Date.now());
                  const timeoutMs = 250;
                  const roles = Object.keys(scheduledRef.current || {});
                  while (true) {
                    if (mySwapId !== swapIdRef.current) break;
                    try { if (((refs as any)._swapSeq ?? mySeq) !== mySeq) break; } catch {}
                    const allLive = roles.every(r => { try { return (scheduledRef.current as any)?.[r]?._rbLive === true; } catch { return false; } });
                    const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
                    if (allLive || (now - start) > timeoutMs) break;
                    await new Promise<void>(r => setTimeout(r, 10));
                  }
                } catch {}
              }

              // Reapply last known tempo to all RB nodes (preserve speed across scopes)
              try {
                const lastTempo = (refs as any).rbTempoRef?.current;
                if (lastTempo != null) {
                  const sched = scheduledRef.current;
                  for (const role of Object.keys(sched)) {
                    try { sched[role]?.worklet?.node?.port?.postMessage({ type: 'tempo', value: lastTempo }); } catch {}
                  }
                }
              } catch {}

              // Reapply last known pitch (semitone-derived scale) to all RB nodes
              try {
                const st = (refs as any).pitchStRef?.current ?? 0;
                const scale = Math.pow(2, (Number(st) || 0) / 12);
                const sched = scheduledRef.current;
                for (const role of Object.keys(sched)) {
                  try { sched[role]?.worklet?.node?.port?.postMessage({ type: 'pitch', value: scale }); } catch {}
                }
              } catch {}

              // Ensure RB pitch/tempo and WA playbackRate reflect current mapping and user pitch
              try {
                const userPitch = Math.pow(2, (((refs as any).pitchStRef?.current) || 0) / 12);
                const sched = scheduledRef.current;
                // Apply mapping immediately based on maintainPitch, without waiting for RB to report 'live'
                const effMP = !!maintainPitch;
                const map = computeSpeedMapping(masterPlaybackRate, effMP);
                if (effMP) {
                  if (masterPlaybackRate <= 1.0 + 1e-3) {
                    for (const s of Object.values(sched)) { (s as any).worklet?.setTempo?.(map.rbTempo); (s as any).worklet?.setPitch?.(userPitch); }
                    try { (refs as any).rbTempoRef = (refs as any).rbTempoRef || { current: null }; (refs as any).rbTempoRef.current = map.rbTempo; } catch {}
                    try { (window as any).__rbLastTempo = map.rbTempo; (window as any).__rbLastPitch = userPitch; } catch {}
                  } else {
                    const combined = map.rbPitch * userPitch;
                    for (const s of Object.values(sched)) { (s as any).worklet?.setTempo?.(1.0); (s as any).worklet?.setPitch?.(combined); }
                    try { (refs as any).rbTempoRef = (refs as any).rbTempoRef || { current: null }; (refs as any).rbTempoRef.current = 1.0; } catch {}
                    try { (window as any).__rbLastTempo = 1.0; (window as any).__rbLastPitch = userPitch; } catch {}
                  }
                } else {
                  // No RB present: neutralize RB params if any, and rely on WA playbackRate below
                  for (const s of Object.values(sched)) { (s as any).worklet?.setTempo?.(1.0); (s as any).worklet?.setPitch?.(1.0); }
                  try { (window as any).__rbLastTempo = 1.0; (window as any).__rbLastPitch = 1.0; } catch {}
                }
                // If sources exist (later), ensure WA playbackRate matches mapping
                try {
                  const now = ctx2.currentTime;
                  for (const s of Object.values(sched)) {
                    const stem: any = s;
                    const pr: AudioParam | undefined = (stem.source as any)?.playbackRate;
                    if (pr) {
                      try {
                        pr.cancelScheduledValues(now);
                        pr.setValueAtTime(map.webAudioRate, now);
                      } catch {}
                    }
                  }
                } catch {}
              } catch {}

              const sched = scheduledRef.current;
              const ana: Record<string, AnalyserNode> = {} as any;
              for (const role of Object.keys(sched)) { ana[role] = sched[role].analyser; }
              refs.analyzersRef.current = ana;
              scheduledMetaRef.current = { version: thisScope, sig };
            }

            setDuration(maxDur);

            const canAutoStart = (resumeAfterLoadRef.current) && !userPausedRef.current;
            if (canAutoStart && scheduledRef.current && ctx2) {
              try {
                const ua: any = (typeof navigator !== 'undefined') ? (navigator as any).userActivation : undefined;
                const canResume = !!ua && (ua.isActive === true || ua.hasBeenActive === true);
                if (ctx2.state === 'suspended' && canResume) { await ctx2.resume().catch(() => {}); L.info('media:transport','ctx.resume'); }
              } catch {}
              if (mySwapId !== swapIdRef.current) return; // aborted by a newer swap
              try { pauseAll(); } catch {}
              // Use transport gate to mute before any scheduling; memoize gate fns
              (refs as any)._gateFnsRef = (refs as any)._gateFnsRef || { current: null };
              if (!(refs as any)._gateFnsRef.current) (refs as any)._gateFnsRef.current = makeTransportGate(refs);
              const { closeTransportGate, openTransportGate } = (refs as any)._gateFnsRef.current;
              try { closeTransportGate(); } catch {}
              // Initialize a clock for auto-resume
              try {
                const rateForClock = masterPlaybackRate;
                const startMediaTime = logicalStartRef.current;
                (refs as any).transportClockRef = (refs as any).transportClockRef || { current: null };
                (refs as any).transportClockRef.current = { t0Audio: ctx2.currentTime, t0Media: startMediaTime, rate: rateForClock } as any;
              } catch {}
              const fn = (refs as any).reanchorClockAndReschedule as (() => number | void) | undefined;
              // Enforce minimum epsilon so starts are never scheduled in the past
              const epsilonSec = 0.03;
              const leadSec = Math.max(0.05, epsilonSec);
              const when = typeof fn === 'function' ? (fn() as number | undefined) : scheduleStartAll(
                ctx2,
                scheduledRef.current,
                leadSec,
                logicalStartRef.current,
                masterPlaybackRate,
                undefined,
                { fixedLatencySec: maintainPitch ? 0.04 : 0, clock: (refs as any).transportClockRef?.current }
              );
              if (typeof when === 'number') startedAtRef.current = when;
              // Immediately ensure any recreated sources are connected through RB
              try { reattachRbIfNeeded(ctx2, scheduledRef.current); } catch {}
              try { L.info('media:transport', 'scheduled', { when, logicalStart: logicalStartRef.current, rate: masterPlaybackRate }); } catch {}
              // Post-start health probe loop (opens gate when signal actually appears)
              (refs as any)._postProbeId = ((refs as any)._postProbeId ?? 0) + 1;
              const postProbeId = (refs as any)._postProbeId;
              try { L.info('media:gateprobe', 'probe-meta', { postProbeId, swapId: swapIdRef.current }); } catch {}
              try {
                const an = (refs.masterAnalyserPreRef?.current ?? refs.masterAnalyserRef?.current) as AnalyserNode | null;
                if (an) {
                  setTimeout(() => {
                    if (((refs as any)._postProbeId) !== postProbeId) return; // abort stale/duplicate probe
                    try { if (((refs as any)._swapSeq ?? mySeq) !== mySeq) return; } catch {}
                    const rmsDb = probeRmsDb(an);
                    const ctxState = an.context.state as any;
                    const vol = refs.gateGainRef?.current?.gain?.value ?? refs.masterGainRef?.current?.gain?.value ?? 0;
                    // Throttle to suppress duplicate logs under StrictMode/double-mount
                    try {
                      const sayPostStart = L.throttled('media:health', 'post-start', 250);
                      sayPostStart('info', 'post-start', { ctxState, vol, rmsDb: Number.isFinite(rmsDb as any) ? (rmsDb as number).toFixed(1) : null });
                    } catch {
                      try { L.info('media:health', 'post-start', { ctxState, vol, rmsDb: Number.isFinite(rmsDb as any) ? (rmsDb as number).toFixed(1) : null }); } catch {}
                    }
                    // Log gate/master gids to detect wrong-node opens
                    try {
                      L.info('media:gate', 'probe-start', {
                        gateGid: (refs.gateGainRef?.current as any)?.__gid ?? 'none',
                        masterGid: (refs.masterGainRef?.current as any)?.__gid ?? 'none',
                      });
                    } catch {}
                    try {
                      openGateWhenSignal(refs, {
                        thresholdDb: -85,
                        timeoutMs: 4000,
                        intervalMs: 120,
                        rampMs: 80,
                        onTimeout: async () => {
                          // Force-open the gate so the user hears something immediately
                          try { openTransportGate(120); } catch {}
                          try { if (diagOn()) { logUnexpectedSilence(refs, 'probe-timeout'); logMasterChain(refs, 'probe-timeout'); logScheduled(refs, 'probe-timeout'); } } catch {}
                          // Re-check after a short delay; if still effectively silent, escalate
                          setTimeout(async () => {
                            try {
                              const an2 = (refs.masterAnalyserRef?.current ?? null) as AnalyserNode | null;
                              const db = an2 ? probeRmsDb(an2) : null;
                              if (db == null || db < -75) {
                                try { logUnexpectedSilence(refs, 'escalate-coldStart', { db }); logMasterChain(refs, 'escalate-coldStart'); logScheduled(refs, 'escalate-coldStart'); } catch {}
                                await coldStart(refs, {
                                  hard: true,
                                  bufferModeRef,
                                  scheduledRef,
                                  disposeScheduled,
                                  stopAll,
                                  pauseAll,
                                  setStatus,
                                  setCurrentTime: setCurrentTime || (()=>{}),
                                });
                                // Reacquire and resume a live context after hard flush
                                try {
                                  const ctx3 = getCtx();
                                  refs.audioCtxRef.current = ctx3;
                                  // Only attempt resume if a user gesture has occurred
                                  try {
                                    const ua: any = (typeof navigator !== 'undefined') ? (navigator as any).userActivation : undefined;
                                    const canResume = !!ua && (ua.isActive === true || ua.hasBeenActive === true);
                                    if (ctx3.state === 'suspended' && canResume) { await ctx3.resume().catch(() => {}); }
                                  } catch {}
                                } catch {}
                              }
                            } catch {}
                          }, 800);
                        }
                      });
                    } catch {}
                  }, 350);
                }
              } catch {}
              // After scheduling starts, re-affirm destination hop
              try { ensureDestinationAlive(ctx2, { masterGainRef: refs.masterGainRef, masterHighpassRef: refs.masterHighpassRef, masterFilterRef: refs.masterFilterRef }); } catch {}
              // After schedule, push mapping again to ensure WA rate and RB params match
              try {
                const userPitch = Math.pow(2, (((refs as any).pitchStRef?.current) || 0) / 12);
                // Compute mapping based on maintainPitch immediately after schedule (no need to wait for RB 'live')
                const map = computeSpeedMapping(masterPlaybackRate, !!maintainPitch);
                const sched = scheduledRef.current;
                if (maintainPitch) {
                  if (masterPlaybackRate <= 1.0 + 1e-3) {
                    for (const s of Object.values(sched)) { (s as any).worklet?.setTempo?.(map.rbTempo); (s as any).worklet?.setPitch?.(userPitch); }
                  } else {
                    const combined = map.rbPitch * userPitch;
                    for (const s of Object.values(sched)) { (s as any).worklet?.setTempo?.(1.0); (s as any).worklet?.setPitch?.(combined); }
                  }
                }
                try {
                  const now = ctx2.currentTime;
                  for (const s of Object.values(sched)) {
                    const stem: any = s;
                    const pr: AudioParam | undefined = (stem.source as any)?.playbackRate;
                    if (pr) {
                      try {
                        pr.cancelScheduledValues(now);
                        pr.setValueAtTime(map.webAudioRate, now);
                      } catch {}
                    }
                  }
                } catch {}
              } catch {}
              try { logScheduled(refs, 'after-applyMapping'); } catch {}
              setStatus((p: any) => (p === 'playing' ? p : 'playing'));
              // Start a light “gap” monitor only while playing (case 2)
              try {
                startGapMonitor(
                  refs.masterAnalyserRef.current,
                  () => ({
                    maintainPitch: !!maintainPitch,
                    pitchSt: (refs as any).pitchStRef?.current ?? 0,
                    rate: masterPlaybackRate,
                    expectedPlaying: (args.status === 'playing'),
                  }),
                  () => {
                    // Compute media offset: (ctxTime - startedAt) + logicalStart
                    try {
                      const ctx2b = refs.audioCtxRef.current as AudioContext | null;
                      if (!ctx2b) return;
                      const startedAt = startedAtRef.current || 0;
                      const base = Math.max(0, (ctx2b.currentTime - startedAt)) + (logicalStartRef.current || 0);
                      // Reschedule entire bundle at computed offset
                      const lead = 0.05;
                      const when = scheduleStartAll(
                        ctx2b,
                        scheduledRef.current!,
                        lead,
                        base,
                        masterPlaybackRate,
                        undefined,
                        { seamlessSwap: true, fixedLatencySec: maintainPitch ? 0.04 : 0 }
                      );
                      if (typeof when === 'number') startedAtRef.current = when;
                      try { L.info('media:gap', 'resync.done', { base: Number(base.toFixed?.(3) ?? base), when }); } catch {}
                    } catch {}
                  }
                );
              } catch {}
              // Heartbeat: if meters show healthy but gate closed, or quiet meters while scheduled exists
              try {
                if (diagOn()) {
                  const hbId = ++(refs as any)._hbId || (((refs as any)._hbId = 1), 1);
                  let alive = true;
                  const loop = () => {
                    if ((refs as any)._hbId !== hbId) return; // superseded
                    if (!alive || ctx2.state !== 'running') return;
                    const an = (refs.masterAnalyserRef?.current ?? null) as AnalyserNode | null;
                    let db: number | null = null;
                    try { db = an ? probeRmsDb(an) : null; } catch {}
                    const gateVal = refs.gateGainRef?.current?.gain?.value ?? refs.masterGainRef?.current?.gain?.value ?? null;
                    if ((db != null && db > -30 && (gateVal == null || gateVal < 0.01)) ||
                        (db != null && db < -80 && scheduledRef?.current && Object.keys(scheduledRef.current).length)) {
                      logUnexpectedSilence(refs, 'heartbeat', { db, gateVal });
                      logMasterChain(refs, 'heartbeat');
                      logScheduled(refs, 'heartbeat');
                    }
                    setTimeout(loop, 1500);
                  };
                  alive = true; setTimeout(loop, 2000);
                }
              } catch {}
            } else {
              if (status === 'loading') setStatus((p: any) => (p === 'paused' ? p : 'paused'));
            }
            resumeAfterLoadRef.current = false;
          } catch (e) {
            if (thisRun !== undefined && loadRunIdRef.current !== thisRun) return;
            if (thisScope !== undefined && scopeVersionRef.current !== thisScope) return;
            // Record per-stem error so UI can show a small warning with retry
            try {
              const err: any = e || {};
              const status: number | undefined = (typeof err.status === 'number') ? err.status : undefined;
              const msg: string = String(err?.message || 'Failed to load audio');
              // Best-effort URL extraction from error message
              const urls = tracks.map(t => t.url);
              const matchedUrl = urls.find(u => msg.includes(u));
              const role = matchedUrl ? (tracks.find(t => t.url === matchedUrl)?.role || 'unknown') : (tracks[0]?.role || 'unknown');
              args.setStemErrors?.({ [role]: { status, message: msg, url: matchedUrl } });
            } catch {}
            console.warn('[mix] Buffer load failed:', e);
          }
        })();

      // CASE 2: Same tracks signature → rebuild after Stop
      } else if (buffersRef.current && !scheduledRef.current && refs.masterGainRef.current) {
        try {
          // Ensure the context is running on rebuild only if user has interacted (avoid autoplay warnings)
          try {
            const ua: any = (typeof navigator !== 'undefined') ? (navigator as any).userActivation : undefined;
            const canResume = !!ua && (ua.isActive === true || ua.hasBeenActive === true);
            if (ctx.state === 'suspended' && canResume) { void ctx.resume().catch(() => {}); }
          } catch {}
          // Reaffirm master chain topology and analyser taps before creating scheduled stems
          try {
            ensureMasterChainIdempotent(
              ctx,
              {
                masterGainRef: refs.masterGainRef,
                gateGainRef: refs.gateGainRef,
                teeGainRef: refs.teeGainRef,
                masterAnalyserPreRef: refs.masterAnalyserPreRef,
                masterHighpassRef: refs.masterHighpassRef,
                masterFilterRef: refs.masterFilterRef,
                masterAnalyserRef: refs.masterAnalyserRef,
              },
              { masterVolume, masterFftOrder, masterHighpassHz, masterLowpassHz, q: eqQ },
              { eqEnabled, eqHpfEnabled, eqLpfEnabled, freqResponseEnabled: (freqResponseEnabled || refs?.masterEnvelopeEnabled) }
            );
            ensureDestinationAlive(ctx, { masterGainRef: refs.masterGainRef, masterHighpassRef: refs.masterHighpassRef, masterFilterRef: refs.masterFilterRef });
          } catch {}
          // If anything somehow remains scheduled, dispose it before rebuilding
          try {
            if (scheduledRef.current) {
              const keys = Object.keys(scheduledRef.current || {});
              L.info('media:scope', 'disposing-before-rebuild', { roles: keys });
              try { stopAll(scheduledRef.current); } catch {}
              disposeScheduled(scheduledRef.current);
              scheduledRef.current = null;
            }
          } catch {}
          if (buffersRef.current) {
            // Validate buffers before rebuilding scheduled stems
            try {
              for (const [role, buf] of Object.entries(buffersRef.current as any)) {
                const dur = (buf as any)?.duration;
                if (!buf || typeof dur !== 'number' || !isFinite(dur) || dur <= 0) {
                  try { L.error('media:load', 'invalid-buffer', { role, hasBuf: !!buf, duration: dur ?? null, phase: 'rebuild' }); } catch {}
                  scheduledRef.current = null;
                  setStatus((p: any) => (p === 'paused' ? p : 'paused'));
                  setDuration(0);
                  return;
                }
              }
            } catch {}
            // Reset gate/analyser diagnostic state before recreating scheduled sources
            try { (refs as any)._silentStarts = 0; } catch {}
            try { (refs as any)._gateProbeLastDb = null; } catch {}
            try { (refs as any)._gateProbeLastLogTs = 0; } catch {}
            try { (refs as any)._hbId = 0; } catch {}
            scheduledRef.current = makeScheduled(
              ctx,
              buffersRef.current,
              tracks.map(t => t.role),
              refs.masterGainRef.current
            );
            // Ensure RB remains in path for newly created sources
            try { reattachRbIfNeeded(ctx, scheduledRef.current); } catch {}
          }
        } catch {}

        if (scheduledRef.current) {
          const sched = scheduledRef.current;
          // Populate analyser map immediately so UI meters have data even before play
          try {
            const ana: Record<string, AnalyserNode> = {} as any;
            for (const role of Object.keys(sched)) ana[role] = sched[role].analyser;
            refs.analyzersRef.current = ana;
          } catch {}
          scheduledMetaRef.current = {
            version: scopeVersionRef.current,
            sig: buffersSigRef.current,
          };

          // Attach worklets and apply mapping after they are ready
          ;(async () => {
            try { if (maintainPitch) await ensureRubberBandWorklet(refs.audioCtxRef.current!, scheduledRef.current); } catch {}
            try {
              const lastTempo = (refs as any).rbTempoRef?.current;
              if (lastTempo != null) {
                for (const role of Object.keys(scheduledRef.current)) {
                  try { scheduledRef.current[role]?.worklet?.node?.port?.postMessage({ type: 'tempo', value: lastTempo }); } catch {}
                }
              }
            } catch {}
            // Reapply last known pitch (semitone-derived scale) after attaching RB
            try {
              const st = (refs as any).pitchStRef?.current ?? 0;
              const scale = Math.pow(2, (Number(st) || 0) / 12);
              for (const role of Object.keys(scheduledRef.current)) {
                try { scheduledRef.current[role]?.worklet?.node?.port?.postMessage({ type: 'pitch', value: scale }); } catch {}
              }
            } catch {}
            try {
              const userPitch = Math.pow(2, (((refs as any).pitchStRef?.current) || 0) / 12);
              const sc = scheduledRef.current;
              // Apply mapping immediately based on maintainPitch (do not wait for RB 'live')
              const effMP = !!maintainPitch;
              const map = computeSpeedMapping(masterPlaybackRate, effMP);
              if (effMP) {
                if (masterPlaybackRate <= 1.0 + 1e-3) {
                  for (const s of Object.values(sc)) { (s as any).worklet?.setTempo?.(map.rbTempo); (s as any).worklet?.setPitch?.(userPitch); }
                  try { (refs as any).rbTempoRef = (refs as any).rbTempoRef || { current: null }; (refs as any).rbTempoRef.current = map.rbTempo; } catch {}
                } else {
                  const combined = map.rbPitch * userPitch;
                  for (const s of Object.values(sc)) { (s as any).worklet?.setTempo?.(1.0); (s as any).worklet?.setPitch?.(combined); }
                  try { (refs as any).rbTempoRef = (refs as any).rbTempoRef || { current: null }; (refs as any).rbTempoRef.current = 1.0; } catch {}
                }
              } else {
                for (const s of Object.values(sc)) { (s as any).worklet?.setTempo?.(1.0); (s as any).worklet?.setPitch?.(1.0); }
              }
            } catch {}
          })();
        }
      }
    }
  }, [
    // Avoid using `refs` and callbacks in deps; they are mutable/stable and can cause render loops.
    tracks, masterVolume, masterFftOrder, masterHighpassHz, masterLowpassHz,
    eqQ, eqEnabled, eqHpfEnabled, eqLpfEnabled, freqResponseEnabled,
  ]);
}
